import React, { useEffect, useState } from "react";

// STYLES/COMPONENTS LOCAL
import KogModalSideComponent from "@/main/components/modal/ModalSide";
import * as S from "./styles";
import { getDefaultOptionDrop, getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { FormProvider, useForm } from "react-hook-form";
import { KeyValue } from "@/main/models/common";
import DynamicFiltersExport from "./dinamicFiltersExport/dynamicFiltersExport";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import { getCountriesUser, useCountryReducer } from "@/main/store/country";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import { useDispatch } from "react-redux";
import { procurementReportApi } from "@/main/api";
import { DynamicReport_VariableResponse, DynamicReportGridResponse } from "@/main/models/response";
import { HideDiv, SpinnerLoading } from "@/main/components/shared/styles";
import { VariableTypeEnum } from "@/main/services/enum/VariableTypeEnum";
import { formatFrontEndToBackEndDynamicReport, validateFormDynamicReport } from "./utilsExportDynamicReport";
import { firstLetterToUpperCase } from "@/main/services/utilsString";
import { useParameterByTypeScreenReducer } from "@/main/store/parameterByTypeScreen";
import { ParameterTypeEnum } from "@/main/services/enum/ParameterTypeEnum";
import { toast } from "react-toastify";

interface IModalExportDynamicReportProps {
	isOpenModal: boolean;
	guidDynamicReport: string;
	dynamicReport: DynamicReportGridResponse;
	setIsOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
	closeModal: () => void;
}

export interface IFilterDinamic {
	guidDynamicReportVariable: string;
	idVariableType: VariableTypeEnum;
	value: string | number | boolean | null | KeyValue | KeyValue[] | Date;
	valueRange?: string | number | boolean | null | Date;
	responseServer: DynamicReport_VariableResponse;
}

const INITIAL_FORM_STATE_DEFAULT = {
	country: null as KeyValue | null,
	filtersDinamics: [] as IFilterDinamic[],
};

const INITIAL_FORM_STATE = {
	...INITIAL_FORM_STATE_DEFAULT,
};
export type IFormFiltersDynamicExport = typeof INITIAL_FORM_STATE;

function ModalExportDynamicReport({ guidDynamicReport }: IModalExportDynamicReportProps) {
	// Get the timeout, increase 10 seconds (this is the query timeout, the whole process may take a little longer) and convert to miliseconds
	const parameterTimeoutAPI = (useParameterByTypeScreenReducer(ParameterTypeEnum.DynamicReportTimeout) + 10) * 1000;
	const dispatch = useDispatch();
	const methods = useForm<IFormFiltersDynamicExport>({
		defaultValues: INITIAL_FORM_STATE,
	});
	const { control, handleSubmit, setValue } = methods;
	const { countriesUser: countryReducer } = useCountryReducer();
	const optionDefaultCountry = getDefaultOptionDrop(countryReducer.data);

	const [isLoadingGet, setIsLoadingGet] = useState<boolean>(false);
	const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
	const [dataDynamicFilters, setDataDynamicFilters] = useState<DynamicReport_VariableResponse[]>([]);

	function onSubmitExport(data: IFormFiltersDynamicExport) {
		validateFormDynamicReport(data);
		const request = formatFrontEndToBackEndDynamicReport(guidDynamicReport, data);

		setIsLoadingSave(true);
		procurementReportApi
			.exportDynamicReport(request, parameterTimeoutAPI)
			.then((response) => {
				const result = response.data.result;
				if (result) {
					toast.success(getTranslateByKey("information.reportWillBeEmailedShortly"));
				}
			})
			.finally(() => setIsLoadingSave(false));
	}

	function loadDynamicFilters() {
		setIsLoadingGet(true);
		procurementReportApi
			.getDynamicReportFilters(guidDynamicReport)
			.then((response) => {
				const {
					data: { result },
				} = response;
				setDataDynamicFilters(result);
			})
			.finally(() => setIsLoadingGet(false));
	}

	// #region UseEffects
	useEffect(() => {
		if (isNullUndefined(countryReducer.data)) dispatch(getCountriesUser());
	}, []);

	useEffect(() => {
		if (!isNullUndefined(guidDynamicReport)) {
			loadDynamicFilters();
		}
	}, [guidDynamicReport]);
	// #endregion

	const disabledCountry = isNullUndefined(countryReducer.data) || countryReducer?.data?.length === 1;
	if (isLoadingGet) return <SpinnerLoading />;

	return (
		<FormProvider {...methods}>
			<form onSubmit={handleSubmit(onSubmitExport)} noValidate>
				<S.Container>
					<HideDiv $show={false}>
						<KogAutoCompleteControlled
							id="id-mappingCodes-select-country"
							nameCustom="country"
							label={getTranslateByKey("information.country")}
							isDefaultValueUnique
							valueDefaultKey={optionDefaultCountry?.key}
							control={control}
							rulesCustom={{ required: false }}
							setValueCustom={setValue}
							options={countryReducer.data}
							isLoading={countryReducer.isLoading}
							disabled={disabledCountry}
							onChangeCustom={() => {}}
						/>
					</HideDiv>

					<DynamicFiltersExport
						keyReducer={`id-mappingCodes-modalMapCodeEdit`}
						INITIAL_FORM_STATE_DEFAULT={INITIAL_FORM_STATE_DEFAULT}
						dataDynamicFilters={dataDynamicFilters}
					/>

					<S.AreaButtons>
						<SecondaryButton
							id="id-dynamicReport-btn-save"
							type="button"
							onClick={() => handleSubmit(onSubmitExport)()}
							isLoading={isLoadingSave}
						>
							{firstLetterToUpperCase(getTranslateByKey("information.send"))}
						</SecondaryButton>
					</S.AreaButtons>
				</S.Container>
			</form>
		</FormProvider>
	);
}

function WrapperModalExportDynamicReport(props: IModalExportDynamicReportProps) {
	return (
		<KogModalSideComponent
			shouldRenderOpen
			title={getTranslateByKey("information.filters")}
			open={props.isOpenModal}
			closeModal={() => props.setIsOpenModal(false)}
		>
			<ModalExportDynamicReport {...props} />
		</KogModalSideComponent>
	);
}

export default WrapperModalExportDynamicReport;
